import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Users,
  Phone,
  Clock,
  ShoppingBag,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Filter,
  RefreshCw,
  Search,
  ArrowLeft,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  no_show: "bg-stone-100 text-stone-600 border-stone-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
  preparing: "bg-amber-50 text-amber-700 border-amber-200",
  ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  collected: "bg-blue-50 text-blue-700 border-blue-200",
};

const fmtDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const BookingCard = ({ b, onStatus }) => (
  <div
    data-testid={`booking-card-${b.id.slice(0, 8)}`}
    className="bg-white rounded-2xl border border-stone-200 p-4 hover:border-stone-300 transition-colors"
  >
    <div className="flex items-start justify-between mb-2">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">
          {b.restaurant_name}
        </div>
        <div className="font-serif text-lg text-stone-900 leading-tight truncate">
          {b.name}
        </div>
      </div>
      <span
        className={`shrink-0 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${
          STATUS_STYLES[b.status] || "bg-stone-100 text-stone-600"
        }`}
      >
        {b.status}
      </span>
    </div>

    <div className="grid grid-cols-2 gap-2 text-[13px] text-stone-600 mb-3">
      <div className="flex items-center gap-1.5">
        <Calendar className="w-3.5 h-3.5 text-stone-400" />
        <span className="font-semibold">{b.date}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5 text-stone-400" />
        <span className="font-semibold">{b.time}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5 text-stone-400" />
        {b.party_size} guests
      </div>
      <a href={`tel:${b.phone}`} className="flex items-center gap-1.5 hover:text-stone-900">
        <Phone className="w-3.5 h-3.5 text-stone-400" />
        {b.phone}
      </a>
    </div>

    {b.notes && (
      <div className="text-[12px] text-stone-500 italic bg-stone-50 rounded-lg px-2.5 py-1.5 mb-3">
        “{b.notes}”
      </div>
    )}

    <div className="flex gap-1.5">
      {b.status !== "confirmed" && (
        <button
          data-testid={`confirm-${b.id.slice(0, 8)}`}
          onClick={() => onStatus(b.id, "confirmed")}
          className="flex-1 px-2 py-1.5 rounded-lg bg-emerald-600 text-white text-[12px] font-semibold flex items-center justify-center gap-1 hover:bg-emerald-700"
        >
          <CheckCircle2 className="w-3.5 h-3.5" /> Confirm
        </button>
      )}
      {b.status !== "cancelled" && (
        <button
          onClick={() => onStatus(b.id, "cancelled")}
          className="px-2 py-1.5 rounded-lg border border-stone-200 text-stone-600 text-[12px] font-semibold flex items-center justify-center gap-1 hover:border-rose-300 hover:text-rose-600"
        >
          <XCircle className="w-3.5 h-3.5" /> Cancel
        </button>
      )}
      {b.status === "confirmed" && (
        <button
          onClick={() => onStatus(b.id, "no_show")}
          className="px-2 py-1.5 rounded-lg border border-stone-200 text-stone-600 text-[12px] font-semibold flex items-center justify-center gap-1 hover:border-stone-400"
        >
          No-show
        </button>
      )}
    </div>
    <div className="text-[10px] text-stone-300 mt-2 font-mono">
      ID {b.id.slice(0, 8)} · received {fmtDateTime(b.created_at)}
    </div>
  </div>
);

const OrderCard = ({ o, onStatus }) => (
  <div
    data-testid={`order-card-${o.id.slice(0, 8)}`}
    className="bg-white rounded-2xl border border-stone-200 p-4"
  >
    <div className="flex items-start justify-between mb-2">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">
          {o.restaurant_name}
        </div>
        <div className="font-serif text-lg text-stone-900 leading-tight truncate">
          {o.name}
        </div>
      </div>
      <span
        className={`shrink-0 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${
          STATUS_STYLES[o.status] || "bg-stone-100 text-stone-600"
        }`}
      >
        {o.status}
      </span>
    </div>

    <div className="grid grid-cols-2 gap-2 text-[13px] text-stone-600 mb-2">
      <div className="flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5 text-stone-400" />
        <span className="font-semibold">{o.pickup_time}</span>
      </div>
      <a href={`tel:${o.phone}`} className="flex items-center gap-1.5 hover:text-stone-900">
        <Phone className="w-3.5 h-3.5 text-stone-400" />
        {o.phone}
      </a>
    </div>

    <div className="space-y-0.5 text-[12px] text-stone-700 mb-3 bg-stone-50 rounded-lg px-2.5 py-2">
      {o.items.map((it, i) => (
        <div key={i} className="flex justify-between">
          <span className="truncate">
            <span className="font-bold tabular-nums">{it.qty}×</span> {it.name}
          </span>
          <span className="font-semibold tabular-nums shrink-0 pl-2">
            ${it.subtotal.toFixed(2)}
          </span>
        </div>
      ))}
      <div className="flex justify-between border-t border-stone-200 pt-1 mt-1">
        <span className="font-bold">Total</span>
        <span className="font-bold tabular-nums">${o.total.toFixed(2)}</span>
      </div>
    </div>

    {o.notes && (
      <div className="text-[12px] text-stone-500 italic mb-2">“{o.notes}”</div>
    )}

    <div className="flex gap-1.5">
      {o.status === "pending" && (
        <button
          onClick={() => onStatus(o.id, "preparing")}
          className="flex-1 px-2 py-1.5 rounded-lg bg-amber-500 text-white text-[12px] font-semibold hover:bg-amber-600"
        >
          Start
        </button>
      )}
      {o.status === "preparing" && (
        <button
          onClick={() => onStatus(o.id, "ready")}
          className="flex-1 px-2 py-1.5 rounded-lg bg-emerald-600 text-white text-[12px] font-semibold hover:bg-emerald-700"
        >
          Mark ready
        </button>
      )}
      {o.status === "ready" && (
        <button
          onClick={() => onStatus(o.id, "collected")}
          className="flex-1 px-2 py-1.5 rounded-lg bg-blue-600 text-white text-[12px] font-semibold hover:bg-blue-700"
        >
          Collected
        </button>
      )}
      {o.status !== "cancelled" && o.status !== "collected" && (
        <button
          onClick={() => onStatus(o.id, "cancelled")}
          className="px-2 py-1.5 rounded-lg border border-stone-200 text-stone-600 text-[12px] font-semibold hover:border-rose-300 hover:text-rose-600"
        >
          Cancel
        </button>
      )}
    </div>
    <div className="text-[10px] text-stone-300 mt-2 font-mono">
      ID {o.id.slice(0, 8)} · received {fmtDateTime(o.created_at)}
    </div>
  </div>
);

export default function AdminBookingsPage() {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterRestaurant, setFilterRestaurant] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [b, o] = await Promise.all([
        fetch(`${API}/api/directory/admin/bookings`).then((r) => r.json()),
        fetch(`${API}/api/directory/admin/pickup-orders`).then((r) => r.json()),
      ]);
      setBookings(b.bookings || []);
      setOrders(o.orders || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000); // poll every 30s
    return () => clearInterval(t);
  }, []);

  const setBookingStatus = async (id, status) => {
    setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
    await fetch(`${API}/api/directory/admin/bookings/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
  };
  const setOrderStatus = async (id, status) => {
    setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
    await fetch(`${API}/api/directory/admin/pickup-orders/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
  };

  const restaurants = useMemo(() => {
    const set = new Map();
    [...bookings, ...orders].forEach((r) => {
      if (!set.has(r.restaurant_id)) set.set(r.restaurant_id, r.restaurant_name);
    });
    return [...set.entries()].map(([id, name]) => ({ id, name }));
  }, [bookings, orders]);

  const visibleBookings = useMemo(() => {
    return bookings.filter(
      (b) =>
        (!filterRestaurant || String(b.restaurant_id) === filterRestaurant) &&
        (statusFilter === "all" || b.status === statusFilter) &&
        (!q || (b.name + " " + (b.phone || "") + " " + b.notes).toLowerCase().includes(q.toLowerCase())),
    );
  }, [bookings, filterRestaurant, statusFilter, q]);

  const visibleOrders = useMemo(() => {
    return orders.filter(
      (o) =>
        (!filterRestaurant || String(o.restaurant_id) === filterRestaurant) &&
        (statusFilter === "all" || o.status === statusFilter) &&
        (!q || (o.name + " " + (o.phone || "") + " " + o.items.map((i) => i.name).join(" ")).toLowerCase().includes(q.toLowerCase())),
    );
  }, [orders, filterRestaurant, statusFilter, q]);

  const stats = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const today = bookings.filter((b) => b.date === todayStr);
    return {
      total: bookings.length,
      today: today.length,
      pending: bookings.filter((b) => b.status === "pending").length,
      todayGuests: today.reduce((s, b) => s + (b.party_size || 0), 0),
      ordersToday: orders.length,
      ordersValueToday: orders.reduce((s, o) => s + (o.total || 0), 0),
    };
  }, [bookings, orders]);

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/directory"
            className="w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-600"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <h1 className="font-serif text-xl text-stone-900 tracking-tight">
            Today's bookings
          </h1>
          <span className="text-[12px] text-stone-400 hidden sm:inline">
            HustlePlate admin
          </span>
          <Link
            to="/admin/templates"
            data-testid="nav-templates"
            className="ml-auto text-[12px] text-stone-500 hover:text-stone-900 underline mr-2 hidden sm:inline"
          >
            Templates →
          </Link>
          <button
            data-testid="admin-refresh"
            onClick={load}
            disabled={loading}
            className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Stats strip */}
        <div className="border-t border-stone-100 overflow-x-auto">
          <div className="max-w-7xl mx-auto px-4 py-3 flex gap-3">
            {[
              { label: "Bookings today", value: stats.today, sub: `${stats.todayGuests} guests` },
              { label: "Pending", value: stats.pending, sub: "needs action" },
              { label: "Total bookings", value: stats.total, sub: "all time (demo)" },
              { label: "Pickup orders", value: stats.ordersToday, sub: `$${stats.ordersValueToday.toFixed(2)}` },
            ].map((s) => (
              <div
                key={s.label}
                className="shrink-0 min-w-[140px] bg-stone-50 rounded-xl px-3 py-2 border border-stone-100"
              >
                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                  {s.label}
                </div>
                <div className="text-2xl font-serif text-stone-900 tabular-nums leading-none mt-0.5">
                  {s.value}
                </div>
                <div className="text-[11px] text-stone-400">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Tab + filter row */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="bg-stone-200 rounded-full p-0.5 flex">
            <button
              data-testid="tab-bookings"
              onClick={() => setTab("bookings")}
              className={`px-4 py-1.5 rounded-full text-[13px] font-semibold ${
                tab === "bookings" ? "bg-white text-stone-900 shadow-sm" : "text-stone-600"
              }`}
            >
              <Calendar className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Bookings <span className="text-stone-400">({bookings.length})</span>
            </button>
            <button
              data-testid="tab-orders"
              onClick={() => setTab("orders")}
              className={`px-4 py-1.5 rounded-full text-[13px] font-semibold ${
                tab === "orders" ? "bg-white text-stone-900 shadow-sm" : "text-stone-600"
              }`}
            >
              <ShoppingBag className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Pickup orders <span className="text-stone-400">({orders.length})</span>
            </button>
          </div>

          <div className="flex-1 min-w-[160px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, phone, item…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-stone-200 text-[13px] focus:outline-none focus:border-stone-400"
            />
          </div>

          <select
            data-testid="filter-restaurant"
            value={filterRestaurant}
            onChange={(e) => setFilterRestaurant(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white border border-stone-200 text-[13px] focus:outline-none focus:border-stone-400"
          >
            <option value="">All restaurants</option>
            {restaurants.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white border border-stone-200 text-[13px] focus:outline-none focus:border-stone-400"
          >
            <option value="all">All statuses</option>
            {tab === "bookings" ? (
              <>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="no_show">No-show</option>
                <option value="completed">Completed</option>
              </>
            ) : (
              <>
                <option value="pending">Pending</option>
                <option value="preparing">Preparing</option>
                <option value="ready">Ready</option>
                <option value="collected">Collected</option>
                <option value="cancelled">Cancelled</option>
              </>
            )}
          </select>
        </div>

        {/* Grid */}
        {tab === "bookings" ? (
          visibleBookings.length === 0 ? (
            <div className="text-center py-20 text-stone-500">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-stone-300" />
              <p className="font-medium text-stone-700 mb-1">No bookings yet</p>
              <p className="text-[13px]">When customers book through a restaurant page, they'll appear here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {visibleBookings.map((b) => (
                <BookingCard key={b.id} b={b} onStatus={setBookingStatus} />
              ))}
            </div>
          )
        ) : visibleOrders.length === 0 ? (
          <div className="text-center py-20 text-stone-500">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 text-stone-300" />
            <p className="font-medium text-stone-700 mb-1">No pickup orders yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {visibleOrders.map((o) => (
              <OrderCard key={o.id} o={o} onStatus={setOrderStatus} />
            ))}
          </div>
        )}

        <p className="text-[11px] text-stone-400 text-center mt-8">
          Auto-refreshes every 30 seconds · Demo data
        </p>
      </div>
    </div>
  );
}
